import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { RobotSuggestionOutDTO } from '@api/ringil-api.v2';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';

/** The three outcomes a rule can reach that need a person. `kind` is a plain string on the wire. */
export const SUGGESTION_AWARD_OFFER = 'AWARD_OFFER';
export const SUGGESTION_RECOMMEND_CARRIERS = 'RECOMMEND_CARRIERS';
export const SUGGESTION_AWARD_REFUSED = 'AWARD_REFUSED';

export interface RobotSuggestions {
  suggestions: RobotSuggestionOutDTO[];
  reload: () => void;
  /**
   * Bookkeeping, not the action: the award or the distribution has already happened through the
   * ordinary endpoint by the time this is called. It is deliberately silent about its own failure
   * — a suggestion whose expedition now has a carrier stops being returned anyway.
   */
  markAccepted: (suggestionId: string) => void;
  dismiss: (suggestionId: string) => void;
}

/**
 * What the robot suggests for one expedition.
 *
 * A separate call from the expedition detail rather than another block on that already-large
 * payload, which also lets the card refresh on its own after an award without re-fetching the
 * whole expedition. A company without the `AI_DISPATCHER` feature gets `[]` rather than an error,
 * so there is no feature gate here.
 */
export const useRobotSuggestions = (expeditionId: string): RobotSuggestions => {
  const api = useContext(ApiContext);
  const [suggestions, setSuggestions] = useState<RobotSuggestionOutDTO[]>([]);

  /**
   * `ApiContext` hands out a fresh object on every render of its provider, so depending on one of
   * its functions would rebuild `reload` every render and re-run the effect below forever. The
   * functions themselves never change behaviour, so the latest object is read through a ref.
   */
  const apiRef = useRef(api);
  apiRef.current = api;

  const reload = useCallback(() => {
    if (!expeditionId) return;
    // Advice is not worth a toast when it cannot be fetched; the block simply stays empty.
    apiRef.current.robotSuggestions(expeditionId, data => setSuggestions(data ?? []), null, { onError: () => undefined });
  }, [expeditionId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // The push payload is a hint, never data — react by refetching, so a lost message costs nothing
  // beyond the card appearing on the next load.
  usePushEvent(
    message => {
      const changed = message.data?.expeditionId ?? message.expeditionId;
      if (!changed || changed === expeditionId) reload();
    },
    { types: ['ai-dispatcher.suggestion'], onReconnect: () => reload() },
  );

  const markAccepted = (suggestionId: string) =>
    apiRef.current.acceptRobotSuggestion(expeditionId, suggestionId, () => reload(), null, { onError: () => reload() });

  const dismiss = (suggestionId: string) => apiRef.current.dismissRobotSuggestion(expeditionId, suggestionId, () => reload());

  return { suggestions, reload, markAccepted, dismiss };
};
